import React, { useEffect, useState } from 'react';
import Icon from 'components/AppIcon';
import Input from 'components/ui/Input';
import Button from 'components/ui/Button';
import { cn } from 'utils/cn';
import { formatKeyForInput, parseKeyFromInput, getFieldKeyInputError } from '../extensions/fieldTokenEditorUtils';

const FIELD_TYPE_OPTIONS = [
  { label: 'Texto', value: 'text' },
  { label: 'Moeda', value: 'currency' },
  { label: 'Data', value: 'date' },
  { label: 'Número', value: 'number' },
];

const FieldCard = ({
  field,
  isActive = false,
  collapseSignal = 0,
  onRename,
  onTypeChange,
  onFieldNameChange,
  onObrigatorioChange,
  onRemove,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [keyInput, setKeyInput] = useState(formatKeyForInput(field.key));

  useEffect(() => {
    setKeyInput(formatKeyForInput(field.key));
  }, [field.key]);

  useEffect(() => {
    if (isActive) setExpanded(true);
  }, [isActive]);

  useEffect(() => {
    if (collapseSignal > 0) setExpanded(false);
  }, [collapseSignal]);

  const handleKeyBlur = () => {
    const parsed = parseKeyFromInput(keyInput);
    const keyError = getFieldKeyInputError(keyInput);

    if (!parsed || keyError) {
      setKeyInput(formatKeyForInput(field.key));
      return;
    }

    if (parsed !== field.key) {
      onRename(field.uuid, parsed);
    }
    setKeyInput(formatKeyForInput(parsed));
  };

  const handleKeyChange = (e) => {
    const raw = e.target.value.toUpperCase();
    setKeyInput(raw);
  };

  const keyError = keyInput.trim() ? getFieldKeyInputError(keyInput) : null;

  const handleKeyKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  return (
    <div
      id={`field-card-${field.uuid}`}
      className={cn(
        'rounded-lg border bg-card shadow-elevation-sm transition-shadow scroll-mt-3',
        isActive
          ? 'border-primary ring-2 ring-primary/40 shadow-md'
          : 'border-border'
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
        aria-expanded={expanded}
      >
        <Icon
          name={expanded ? 'ChevronDown' : 'ChevronRight'}
          size={16}
          className="shrink-0 text-muted-foreground"
        />
        <span className="text-sm font-medium text-foreground truncate">
          {field.key}
        </span>
      </button>

      <div className={cn('px-4 pb-4 space-y-4', !expanded && 'hidden')}>
        <Input
          label="Chave"
          value={keyInput}
          onChange={handleKeyChange}
          onBlur={handleKeyBlur}
          onKeyDown={handleKeyKeyDown}
          placeholder="{CAMPO_ADICIONADO_1}"
          error={keyError}
        />

        <div>
          <label
            htmlFor={`field-type-${field.uuid}`}
            className="text-sm font-medium leading-none text-foreground mb-2 block"
          >
            Tipo
          </label>
          <select
            id={`field-type-${field.uuid}`}
            value={field.type}
            onChange={(e) => onTypeChange(field.uuid, e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {FIELD_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <Input
          label="Nome do Campo"
          value={field.fieldName ?? ''}
          onChange={(e) => onFieldNameChange(field.uuid, e.target.value)}
          placeholder="Ex.: Nome do comprador"
        />

        <label
          htmlFor={`field-obrigatorio-${field.uuid}`}
          className="flex items-center gap-2 cursor-pointer select-none"
        >
          <input
            id={`field-obrigatorio-${field.uuid}`}
            type="checkbox"
            checked={Boolean(field.obrigatorio)}
            onChange={(e) => onObrigatorioChange(field.uuid, e.target.checked)}
            className="h-4 w-4 rounded border border-input bg-background text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
          <span className="text-sm font-medium text-foreground">Obrigatório</span>
        </label>

        <div className="flex justify-end pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconName="Trash2"
            className="text-destructive hover:text-destructive"
            onClick={() => onRemove(field.uuid)}
          >
            Remover
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FieldCard;
